import {
    getRelationshipLabel,
    isValidRelationshipType,
} from "~/features/parts/constants";
import type { ProcessedRelationship } from "~/features/parts/utils/process-relationships";

// AIDEV-NOTE: The session parts map's edge aggregation, lifted out of session-notes.tsx.
// There is deliberately NO parts map on the session part-detail sidebar (Adam, Sep 2026) —
// the map that shows a part's full set of relations lives on /parts/$partId, where the
// neighbourhood is read from the server across every session rather than just this one.

/** A relationship flattened to part ids — what the maps and the force graph both take. */
export interface FlatPartRelationship {
    id: string;
    type: string;
    description: string;
    sourcePartId: string;
    targetPartId: string;
    sourcePartInteractionId: string;
    targetPartInteractionId: string;
}

/** Minimum shape the aggregation needs; real callers pass TPartInteractionWithImage. */
interface RelatablePart {
    id: string;
    partId?: string | null;
    relationships?: unknown;
}

// AIDEV-NOTE: Endpoint resolution for one side of a relationship. The old fallback keyed
// EVERY unresolved endpoint to the carrying part, which collapsed the edge into a
// self-edge whenever the FAR endpoint's part was unresolved (its interaction has partId
// NULL — a real, recurring shape in production). A self-edge draws as a zero-length line
// with the relationship icon stacked on the node: "no line, no other part pictured".
// The far endpoint now keys by its interaction id, which is exactly the node id the maps
// use for a partId-less interaction (`part.partId || part.id`).
function resolveEndpointPartId(
    resolvedPartId: string | undefined,
    endpointInteractionId: string,
    carrier: RelatablePart,
): string {
    if (resolvedPartId) return resolvedPartId;
    if (!endpointInteractionId || endpointInteractionId === carrier.id) {
        return carrier.partId || carrier.id;
    }
    return endpointInteractionId;
}

/**
 * Collect every relationship carried by a session's parts, keyed by relationship id so the
 * same edge reported from both ends appears once.
 */
export function aggregateSessionRelationships(
    parts: RelatablePart[],
): FlatPartRelationship[] {
    const byId = new Map<string, FlatPartRelationship>();

    for (const part of parts) {
        if (!Array.isArray(part.relationships)) continue;
        for (const rel of part.relationships as ProcessedRelationship[]) {
            // AIDEV-NOTE: Convert type to user-facing label if valid.
            const displayType = isValidRelationshipType(rel.relationship.type)
                ? getRelationshipLabel(rel.relationship.type)
                : rel.relationship.type;

            const flat: FlatPartRelationship = {
                id: rel.relationship.id,
                type: displayType,
                description: rel.relationship.description,
                sourcePartId: resolveEndpointPartId(
                    rel.sourcePart?.id,
                    rel.relationship.sourcePartInteractionId,
                    part,
                ),
                targetPartId: resolveEndpointPartId(
                    rel.targetPart?.id,
                    rel.relationship.targetPartInteractionId,
                    part,
                ),
                sourcePartInteractionId:
                    rel.relationship.sourcePartInteractionId,
                targetPartInteractionId:
                    rel.relationship.targetPartInteractionId,
            };

            if (flat.id && !byId.has(flat.id)) byId.set(flat.id, flat);
        }
    }

    return [...byId.values()];
}

// AIDEV-NOTE: An edge whose endpoint has no rendered node makes d3 forceLink throw
// ("node not found"), which used to take the WHOLE map down (error boundary fallback).
// Renderers call this to draw the healthy edges and report the dropped count in the
// render-health telemetry instead of dying.
export function splitRenderableEdges<
    T extends { sourcePartId: string; targetPartId: string },
>(
    edges: T[],
    renderedNodeIds: ReadonlySet<string>,
): { renderable: T[]; danglingEdgeCount: number } {
    const renderable = edges.filter(
        (edge) =>
            renderedNodeIds.has(edge.sourcePartId) &&
            renderedNodeIds.has(edge.targetPartId),
    );
    return { renderable, danglingEdgeCount: edges.length - renderable.length };
}
